import { useRef, useCallback } from 'react';

interface UseScrollNavigationOptions {
  /** Whether the navbar is currently sticky */
  isNavbarSticky: boolean;
  /** Callback to set active category */
  setActiveCategory: (id: string | null) => void;
  /** Ref to mark manual scroll in progress */
  isManualScroll: React.MutableRefObject<boolean>;
  /** Callback to clear pending category changes */
  clearPendingCategory: () => void;
  /** Timeout before resetting manual scroll flag (ms) */
  manualScrollTimeout?: number;
}

interface UseScrollNavigationReturn {
  /** Scroll to top of page */
  scrollToTop: () => void;
  /** Scroll to a specific category */
  scrollToCategory: (categoryId: string) => Promise<void>;
  /** Scroll to "All" (top of page) */
  scrollToAll: () => void;
}

/**
 * Hook for programmatic scroll navigation to categories.
 * Handles lazy-loaded sections and prevents conflicts with automatic scroll detection.
 */
export function useScrollNavigation({
  isNavbarSticky,
  setActiveCategory,
  isManualScroll,
  clearPendingCategory,
  manualScrollTimeout = 1500,
}: UseScrollNavigationOptions): UseScrollNavigationReturn {
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToTop = useCallback(() => {
    isManualScroll.current = true;
    clearPendingCategory();
    setActiveCategory(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Reset manual scroll after animation
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isManualScroll.current = false;
    }, manualScrollTimeout);
  }, [setActiveCategory, isManualScroll, clearPendingCategory, manualScrollTimeout]);

  const scrollToCategory = useCallback(async (categoryId: string) => {
    isManualScroll.current = true;
    clearPendingCategory();
    setActiveCategory(categoryId);

    // Small delay to allow lazy section to render
    await new Promise(resolve => setTimeout(resolve, 50));

    let element = document.getElementById(`category-${categoryId}`);

    // If element doesn't exist yet (lazy loading), wait a bit longer
    if (!element) {
      await new Promise(resolve => setTimeout(resolve, 200));
      element = document.getElementById(`category-${categoryId}`);
    }

    if (element) {
      const yOffset = isNavbarSticky ? -120 : -160;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });
    }

    // Reset manual scroll after animation completes
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isManualScroll.current = false;
    }, manualScrollTimeout);
  }, [isNavbarSticky, setActiveCategory, isManualScroll, clearPendingCategory, manualScrollTimeout]);

  const scrollToAll = useCallback(() => {
    scrollToTop();
  }, [scrollToTop]);

  return {
    scrollToTop,
    scrollToCategory,
    scrollToAll,
  };
}

export default useScrollNavigation;
